/**
 * The gate on a counter's own link.
 *
 * ── The bug this exists to fix ────────────────────────────────────────────
 *
 * An owner copies a worker link — `/w/<branchId>` — and gives it to the
 * counter. The worker opening it is signed out, so the ordinary gate sent them
 * to `/login`, which asks for an email address. A location account has a
 * username and a synthetic address nobody was ever told. There was no way
 * through: the link worked for the owner who copied it and for nobody else,
 * and every dead end landed on CenoX Home.
 *
 * So a signed-out visitor is sent to the sign-in this location actually uses,
 * resolved from the branch in the link itself.
 *
 * ── Why not just change the copied link ───────────────────────────────────
 *
 * Because the link is already printed, pinned up and bookmarked. One address
 * has to work in both states — signed in it opens the workspace, signed out it
 * routes to the right sign-in and comes back — or every link handed out so far
 * stops meaning anything.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Spinner } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { withNext } from '../../lib/intent';

interface WorkerLinkContext {
  businessName: string;
  businessCode: string;
  locationName: string;
}

export function RequireWorkerAuth({ children }: { children: ReactNode }): ReactNode {
  const { user, loading } = useAuth();
  const { branchId } = useParams<{ branchId: string }>();

  const [context, setContext] = useState<WorkerLinkContext | null>(null);
  const [unresolvable, setUnresolvable] = useState(false);

  /*
    Resolve only when signed out.

    A signed-in owner opening the same link needs none of this, and asking
    anyway would spend a request on every visit to tell us something we are
    not going to use.
  */
  const needsSignIn = !loading && !user;

  useEffect(() => {
    if (!needsSignIn || !branchId) return;
    let cancelled = false;

    api
      .get<WorkerLinkContext>(`/auth/location/by-branch/${branchId}`)
      .then((res) => {
        if (!cancelled) setContext(res);
      })
      .catch(() => {
        // A link that resolves to nothing. Fall through to the ordinary
        // sign-in rather than stranding somebody on a spinner — a personal
        // account opening a stale link still belongs somewhere.
        if (!cancelled) setUnresolvable(true);
      });

    return () => {
      cancelled = true;
    };
  }, [needsSignIn, branchId]);

  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner label="Restoring your session" />
      </div>
    );
  }

  if (user) return <>{children}</>;

  const here = branchId ? `/w/${branchId}` : null;

  if (unresolvable || !branchId) {
    return <Navigate to={withNext('/login', here)} replace />;
  }

  if (!context) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Spinner label="Opening this location" />
      </div>
    );
  }

  /*
    To the location's own sign-in, carrying where to come back to.

    `next` survives the sign-in and brings the worker to the workspace they
    were sent a link to, rather than to a general screen they then have to
    navigate out of.
  */
  return <Navigate to={withNext(`/l/${context.businessCode}`, here)} replace />;
}
